import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { computeGiuspFlags } from './validator';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 25,
    fontFamily: 'Helvetica',
    fontSize: 8
  },
  header: {
    marginBottom: 12,
    textAlign: 'center',
    borderBottom: 2,
    paddingBottom: 8
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4
  },
  subtitle: {
    fontSize: 9,
    marginBottom: 2
  },
  section: {
    marginBottom: 8,
    border: 1,
    padding: 6
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 4,
    textAlign: 'center',
    backgroundColor: '#d0d0d0',
    padding: 3,
    marginLeft: -6,
    marginRight: -6,
    marginTop: -6
  },
  row: {
    flexDirection: 'row',
    marginBottom: 3
  },
  label: {
    fontSize: 7,
    fontWeight: 'bold',
    width: '25%'
  },
  value: {
    fontSize: 7,
    width: '75%'
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 8
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
    borderBottom: 1
  },
  tableCol: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    padding: 2
  },
  tableCell: {
    fontSize: 6,
    textAlign: 'center'
  },
  tableHeader: {
    backgroundColor: '#d0d0d0',
    fontWeight: 'bold'
  },
  signatureBox: {
    marginTop: 8,
    padding: 4,
    borderBottom: 1,
    borderColor: '#000',
    minHeight: 35
  },
  signatureImage: {
    maxHeight: 30,
    maxWidth: 180,
    objectFit: 'contain'
  },
  warningBox: {
    border: 1,
    padding: 8,
    marginBottom: 8,
    backgroundColor: '#f5f5f5'
  }
});

const GasSafetyPDF = ({ formData }) => {
  const giusp = computeGiuspFlags(formData);
  const critical = giusp.situation?.severity === 'critical';
  const atRisk = giusp.situation?.severity === 'warning';
  const hasUnsafeFlag = critical || atRisk;

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>LANDLORD/HOME OWNER GAS SAFETY RECORD</Text>
          <Text style={styles.subtitle}>REGISTERED INSPECTOR'S DETAILS</Text>
        </View>

        {/* GIUSP Unsafe Situation Banner (high-visibility) */}
        {giusp.situation && hasUnsafeFlag && (
          <View style={{ backgroundColor: critical ? '#CC0000' : '#FFCC00', border: 2, borderColor: '#000000', padding: 6, marginBottom: 8 }}>
            <Text style={{ fontSize: 9, fontWeight: 'bold', color: critical ? '#FFFFFF' : '#000000', textAlign: 'center' }}>
              {critical ? 'IMMEDIATELY DANGEROUS SITUATION - DO NOT USE' : 'AT RISK SITUATION - WARNING'}
            </Text>
            <Text style={{ fontSize: 6, color: critical ? '#FFFFFF' : '#000000', marginTop: 2, textAlign: 'center' }}>
              {critical
                ? 'Report to Gas Emergency Service Provider: 0800 111 999'
                : 'Appliance At Risk - complete At Risk Warning Notice and turn off with consent.'}
            </Text>
          </View>
        )}

        {hasUnsafeFlag && giusp.flags.length > 0 && (
          <View style={{ backgroundColor: '#ffecb3', padding: 4, marginBottom: 6 }}>
            <Text style={{ fontSize: 6, fontWeight: 'bold' }}>Unsafe items recorded:</Text>
            {giusp.flags.map((flag, i) => (
              <Text key={i} style={{ fontSize: 5, marginBottom: 1 }}>
                - {(flag.location || 'Appliance')}: {flag.reason.join(', ')}
              </Text>
            ))}
          </View>
        )}

        {/* Inspector Details */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.label}>Name:</Text>
            <Text style={styles.value}>{formData.inspector?.name || 'N/A'}</Text>
            <Text style={styles.label}>Gas Safe Reg No.:</Text>
            <Text style={styles.value}>{formData.inspector?.gasRegNumber || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Address:</Text>
            <Text style={styles.value}>{formData.inspector?.address || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Post Code:</Text>
            <Text style={styles.value}>{formData.inspector?.postCode || 'N/A'}</Text>
            <Text style={styles.label}>Tel:</Text>
            <Text style={styles.value}>{formData.inspector?.tel || 'N/A'}</Text>
          </View>
        </View>

        {/* Inspection Address */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>INSPECTION ADDRESS</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Name:</Text>
            <Text style={styles.value}>{formData.inspectionAddress?.name || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Address:</Text>
            <Text style={styles.value}>{formData.inspectionAddress?.address || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Post Code:</Text>
            <Text style={styles.value}>{formData.inspectionAddress?.postCode || 'N/A'}</Text>
            <Text style={styles.label}>Tel:</Text>
            <Text style={styles.value}>{formData.inspectionAddress?.tel || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Tenant's Name:</Text>
            <Text style={styles.value}>{formData.inspectionAddress?.tenantName || 'N/A'}</Text>
            <Text style={styles.label}>Present:</Text>
            <Text style={styles.value}>{formData.inspectionAddress?.tenantPresent || 'N/A'}</Text>
          </View>
        </View>

        {/* Landlord Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>LANDLORD DETAILS</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Name:</Text>
            <Text style={styles.value}>{formData.landlord?.name || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Address:</Text>
            <Text style={styles.value}>{formData.landlord?.address || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Post Code:</Text>
            <Text style={styles.value}>{formData.landlord?.postCode || 'N/A'}</Text>
            <Text style={styles.label}>Tel:</Text>
            <Text style={styles.value}>{formData.landlord?.tel || 'N/A'}</Text>
          </View>
        </View>

        {/* Warning Text */}
        <View style={styles.warningBox}>
          <Text style={{ fontSize: 6, textAlign: 'center' }}>
            This inspection is for gas safety purposes only to comply with the Gas Safety
            (Installation and Use) Regulations. Flues have been inspected visually and checked for
            satisfactory evacuation of products of combustion. A detailed internal inspection of the
            flue integrity, construction and lining has been carried out.
          </Text>
        </View>

        {/* Appliances Table - Compact version to fit all columns */}
        <Text style={{ fontSize: 8, fontWeight: 'bold', marginBottom: 4, textAlign: 'center', backgroundColor: '#d0d0d0', padding: 3 }}>
          APPLIANCE DETAILS & INSPECTION DETAILS
        </Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View style={[styles.tableCol, { width: '4%' }]}>
              <Text style={styles.tableCell}>No.</Text>
            </View>
            <View style={[styles.tableCol, { width: '8%' }]}>
              <Text style={styles.tableCell}>LOC</Text>
            </View>
            <View style={[styles.tableCol, { width: '7%' }]}>
              <Text style={styles.tableCell}>TYPE</Text>
            </View>
            <View style={[styles.tableCol, { width: '8%' }]}>
              <Text style={styles.tableCell}>MAKE</Text>
            </View>
            <View style={[styles.tableCol, { width: '8%' }]}>
              <Text style={styles.tableCell}>MODEL</Text>
            </View>
            <View style={[styles.tableCol, { width: '5%' }]}>
              <Text style={styles.tableCell}>FLUE</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>OP. PRESS</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>HEAT INPUT</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>SMOKE SPILL</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>SMOKE VENT</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>FLUE TERM</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>VISUAL</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>VENT</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>SAFE</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>LL APP</Text>
            </View>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableCell}>INSP</Text>
            </View>
          </View>
          
          {/* Table Data */}
          {formData.appliances && formData.appliances.length > 0 ? (
            formData.appliances.map((appliance, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.tableCol, { width: '4%' }]}>
                  <Text style={styles.tableCell}>{index + 1}</Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={styles.tableCell}>{appliance.location || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '7%' }]}>
                  <Text style={styles.tableCell}>{appliance.type || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={styles.tableCell}>{appliance.make || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={styles.tableCell}>{appliance.model || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '5%' }]}>
                  <Text style={styles.tableCell}>{appliance.flueType || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.operatingPressure || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.heatInputActual || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.smokeMatchSpillage || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.smokeMatchVentilation || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.flueTerminationSatisfactory || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.visualCondition || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.adequateVentilation || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.applianceSafeToUse || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.landlordsAppliance || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCell}>{appliance.inspected || '-'}</Text>
                </View>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>No appliances recorded</Text>
              </View>
            </View>
          )}
        </View>

        {/* Soundness Test */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.label}>Gas Installation Soundness Test:</Text>
            <Text style={[styles.value, { fontWeight: 'bold' }]}>
              {formData.soundnessTest?.result || 'N/A'}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Warning Notice Issued:</Text>
            <Text style={styles.value}>{formData.soundnessTest?.warningNoticeIssued || 'N/A'}</Text>
            <Text style={styles.label}>Warning Tag/Sticker Fitted:</Text>
            <Text style={styles.value}>{formData.soundnessTest?.warningStickerFitted || 'N/A'}</Text>
          </View>
        </View>

        {/* Faults */}
        {formData.faultsAndRectification?.details && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>GIVE DETAILS OF ALL FAULTS AND RECTIFICATION WORK REQUIRED</Text>
            <Text style={{ fontSize: 7 }}>{formData.faultsAndRectification.details}</Text>
          </View>
        )}

        {/* Certification */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.label}>Number of appliances tested:</Text>
            <Text style={styles.value}>{formData.certification?.numberOfAppliances || 0}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>NEXT GAS SAFETY CHECK MUST BE CARRIED OUT WITHIN:</Text>
            <Text style={[styles.value, { fontWeight: 'bold' }]}>{formData.certification?.nextCheckMonths || 12} MONTHS</Text>
          </View>
        </View>

        {/* Issued By */}
        <View style={styles.section}>
          <Text style={{ fontSize: 8, fontWeight: 'bold', marginBottom: 4 }}>This record is issued by:</Text>
          
          {formData.certification?.engineerSignature && (
            <View style={styles.signatureBox}>
              <Text style={{ fontSize: 6, marginBottom: 2 }}>Signed:</Text>
              <Image 
                src={formData.certification.engineerSignature} 
                style={styles.signatureImage}
              />
            </View>
          )}
          
          <View style={styles.row}>
            <Text style={styles.label}>Print name:</Text>
            <Text style={styles.value}>{formData.certification?.engineerPrintName || 'N/A'}</Text>
            <Text style={styles.label}>Date:</Text>
            <Text style={styles.value}>
              {formData.certification?.inspectionDate ? 
                new Date(formData.certification.inspectionDate).toLocaleDateString('en-GB') : 
                'N/A'}
            </Text>
          </View>
        </View>

        {/* Landlord Receipt */}
        <View style={styles.section}>
          <Text style={{ fontSize: 8, fontWeight: 'bold', marginBottom: 4 }}>Received on behalf of the landlord:</Text>
          
          {formData.landlordReceipt?.signature && (
            <View style={styles.signatureBox}>
              <Text style={{ fontSize: 6, marginBottom: 2 }}>Signed:</Text>
              <Image 
                src={formData.landlordReceipt.signature} 
                style={styles.signatureImage}
              />
            </View>
          )}
          
          <View style={styles.row}>
            <Text style={styles.label}>Tenant/Agent/Landlord:</Text>
            <Text style={styles.value}>{formData.landlordReceipt?.recipientType || 'N/A'}</Text>
            <Text style={styles.label}>Date:</Text>
            <Text style={styles.value}>
              {formData.landlordReceipt?.receiptDate ? 
                new Date(formData.landlordReceipt.receiptDate).toLocaleDateString('en-GB') : 
                'N/A'}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={{ marginTop: 8, borderTop: 1, paddingTop: 4 }}>
          <Text style={{ fontSize: 5, textAlign: 'center' }}>
            Certificate Number: {formData.certification?.certificateNumber || 'N/A'}
          </Text>
          <Text style={{ fontSize: 5, textAlign: 'center', marginTop: 2 }}>
            Copies: Top Copy - Landlord/Agent | Middle Copy - Installer | Bottom Copy - Tenant
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default GasSafetyPDF;
